import { useApolloClient } from '@graphcommerce/graphql'
import { AccountAddressFragment } from '@graphcommerce/magento-customer/components/AccountAddress/AccountAddress.gql'
import { DeleteCustomerAddressFormDocument } from '@graphcommerce/magento-customer/components/DeleteCustomerAddressForm/DeleteCustomerAddressForm.gql'
import { SectionContainer } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Box, Modal, Paper, Skeleton } from '@mui/material'
import { useCallback, useState } from 'react'
import { Button } from '../../Button'
import { StandardButton } from '../../Button/ButtonStyles'
import { SectionTitle } from '../../Container/SectionTitle'
import { ConfirmationPrompt } from '../../Prompt/ConfirmationPrompt'
import { AddressSkeleton } from '../../Skeleton/AddressSkeleton'
import { AccountAddress } from '../AccountAddress/AccountAddress'
import { CreateCustomerAddressForm } from '../CreateCustomerAddressForm/CreateCustomerAddressForm'
import { EditAddressForm } from '../EditAddressForm/EditAddressForm'
import { AccountAddressFreightFragment } from './AccountAddressFreight.gql'

export type AccountAddressesProps = {
  addresses?: ((AccountAddressFreightFragment & AccountAddressFragment) | null)[]
} & {
  loading: boolean
  refetch: () => void
}

export function AccountAddresses(props: AccountAddressesProps) {
  const { addresses = [], loading, refetch } = props
  const client = useApolloClient()
  const shippingAddresses = addresses.filter((a) => a && !a.is_freight_address)
  const freightAddresses = addresses.filter((a) => a && a.is_freight_address)
  const [selectedShipAddr, setSelectedShipAddr] = useState<null | string | number>(
    shippingAddresses?.at(0)?.id as number,
  )
  const [selectedFreightShipAddr, setSelectedFreightShipAddr] = useState<null | string | number>(
    freightAddresses?.at(0)?.id as number,
  )

  const [showForm, setShowForm] = useState<boolean | string>(false)
  const [isEditingShipping, setIsEditingShipping] = useState<boolean | string>(false)
  const [isEditingFreight, setIsEditingFreight] = useState<boolean | string>(false)

  const [isDeleting, setIsDeleting] = useState<boolean | number>(false)
  const [displayWarning, setIsDisplayWarning] = useState<boolean>(false)
  const [displayFreightWarning, setIsDisplayFreightWarning] = useState<boolean>(false)

  const [response, setResponse] = useState<boolean | undefined>()

  const handleAddressDelete = useCallback(
    async (callbackResponse: boolean, addressId: number) => {
      if (callbackResponse) {
        try {
          setIsDeleting(addressId)
          await client.mutate({
            mutation: DeleteCustomerAddressFormDocument,
            variables: {
              id: addressId,
            },
          })
          setIsDeleting(false)
          refetch()
        } catch (error) {
          console.log('error', error)
          setIsDeleting(false)
        }
      }
    },
    [client, refetch],
  )

  const handleClickDelete = () => {
    setIsDisplayWarning(true)
  }

  if (loading) {
    return (
      <div className='w-full'>
        <Paper elevation={3} className='md:px-15 p-10 md:py-8'>
          <span className='Type-XXL-Regular w-full '>Shipping address</span>
          <hr className='mb-7 mt-2 text-20-grey' />
          <AddressSkeleton />
        </Paper>
      </div>
    )
  }

  return (
    <div className='w-full'>
      <Paper elevation={3} className='md:px-15 p-10 md:py-8'>
        <span className='Type-XXL-Regular w-full '>Shipping address</span>
        <hr className='mb-7 mt-2 text-20-grey' />
        <div className='mb-11'>
          {((addresses && addresses.length === 0) || !addresses) && (
            <div>
              <Trans id='You have no addresses saved yet' />
            </div>
          )}
          {addresses && addresses.length >= 1 && (
            <div className='flex w-fit flex-col gap-5'>
              {addresses?.map((address) => {
                if (address && address.id) {
                  return (
                    <button
                      key={address.id}
                      type='button'
                      onClick={() => setSelectedShipAddr(Number(address.id))}
                      className={`block min-h-[56px] w-full rounded-[4px] p-[12px] pr-[20px] ${
                        address.id === selectedShipAddr
                          ? 'border-[3px] border-material-ui-blue'
                          : 'border-[1px] border-70-grey'
                      }`}
                    >
                      <AccountAddress
                        key={address?.id}
                        {...address}
                        loading={!!isDeleting && isDeleting === address.id}
                      />
                    </button>
                  )
                }
                return null
              })}
            </div>
          )}
        </div>
        <div className='flex w-full flex-col items-center justify-center gap-[30px] md:flex-row md:justify-start'>
          <StandardButton
            type='button'
            className='w-full max-w-[350px] bg-material-ui-blue uppercase md:w-auto'
            variant='contained'
            onClick={() => {
              setSelectedShipAddr(null)
              setIsEditingShipping(true)
              setIsEditingFreight(false)
              setShowForm('shipping')
            }}
          >
            {i18n._('Add Address')}
          </StandardButton>
          <StandardButton
            type='button'
            className='w-full max-w-[350px] bg-material-ui-blue uppercase md:w-auto'
            variant='contained'
            disabled={!selectedShipAddr}
            onClick={() => {
              setIsEditingShipping(true)
              setIsEditingFreight(false)
              setShowForm('shipping')
            }}
          >
            {i18n._('Edit')}
          </StandardButton>
          <StandardButton
            type='button'
            className='w-full max-w-[350px] bg-material-ui-blue uppercase md:w-auto'
            variant='contained'
            disabled={!selectedShipAddr}
            onClick={() => handleClickDelete()}
          >
            {i18n._('Delete')}
          </StandardButton>
        </div>
      </Paper>
      <Modal className='z-10 ' open={!!showForm} onClose={() => setShowForm(false)}>
        <div className='absolute left-[5vw] top-[5vh] max-h-[600px] w-[90vw] overflow-auto rounded-md border-solid  bg-pure-white md:left-[10vw] md:w-[80vw] lg:left-[20vw] lg:w-[60vw] xl:left-[25vw] xl:w-[50vw]'>
          {isEditingShipping &&
          selectedShipAddr &&
          addresses &&
          addresses.find((addr) => addr?.id === selectedShipAddr) ? (
            <div className='mx-10 my-4'>
              <SectionTitle title='Edit Address' className='mx-0' />

              <EditAddressForm
                address={
                  addresses.find(
                    (addr) => addr?.id === selectedShipAddr,
                  ) as unknown as AccountAddressFragment
                }
                onSuccess={() => {
                  setShowForm(false)
                  refetch()
                }}
                onCancel={() => setShowForm(false)}
              />
            </div>
          ) : null}
          {isEditingShipping && !selectedShipAddr && (
            <div className='mx-10 my-4'>
              <SectionTitle title='Add shipping address' className='mx-0' />

              <CreateCustomerAddressForm
                onSuccess={() => {
                  setShowForm(false)
                  refetch()
                }}
                onComplete={() => {}}
                isFreight={showForm === 'freight'}
                onCancel={() => setShowForm(false)}
              />
            </div>
          )}
          {isEditingFreight &&
          selectedFreightShipAddr &&
          addresses &&
          addresses.find((addr) => addr?.id === selectedFreightShipAddr) ? (
            <div className='mx-10 my-4'>
              <SectionTitle title='Edit Address' className='mx-0' />

              <EditAddressForm
                address={
                  addresses.find(
                    (addr) => addr?.id === selectedFreightShipAddr,
                  ) as unknown as AccountAddressFragment
                }
                onSuccess={() => {
                  setShowForm(false)
                  refetch()
                }}
                onCancel={() => setShowForm(false)}
              />
            </div>
          ) : null}
          {isEditingFreight && !selectedFreightShipAddr && (
            <div className='mx-10 my-4'>
              <SectionTitle title='Add shipping address' className='mx-0' />

              <CreateCustomerAddressForm
                onSuccess={() => {
                  setShowForm(false)
                  refetch()
                }}
                onComplete={() => {}}
                isFreight={showForm === 'freight'}
                onCancel={() => setShowForm(false)}
              />
            </div>
          )}
        </div>
      </Modal>
      <ConfirmationPrompt
        title={i18n._('Delete Address')}
        message={i18n._('Do you want to delete this address from your account?')}
        isDisplayed={displayWarning}
        setIsDisplayed={setIsDisplayWarning}
        setResponse={setResponse}
        callback={handleAddressDelete}
        callbackArgs={selectedShipAddr as number}
      />
      <ConfirmationPrompt
        title={i18n._('Delete Address')}
        message={i18n._('Do you want to delete this address from your account?')}
        isDisplayed={displayFreightWarning}
        setIsDisplayed={setIsDisplayFreightWarning}
        setResponse={setResponse}
        callback={handleAddressDelete}
        callbackArgs={selectedFreightShipAddr as number}
      />
    </div>
  )
}
